import { objectHas } from '@app/utils/object'
import { getFormattedDate } from '@app/utils/date'
import { dateFormat } from '@app/constants'
import { VALUE_PLACEHOLDER } from '@app/constants/valuePlaceholder'

import { getExternalProductStatus } from '@app/shared/lib'

import { FormResultsItemPriceData } from '../types'

import type {
  MonitoringPhotos,
  MonitoringTaskResultItemsDto,
  MonitoringTaskResultItemCommentsItemDto
} from '@app/api/routes/monitoringTasks/dtos'
import type { LntImageProps } from '@app/components/base/Image/types'
import type { PatchTaskResultsItemData } from '@app/api/routes/monitoringTasks/types'
import type { CommentData, ExternalProduct, ProcessingResultsData } from '../types'
import type { ExternalProductsListDto } from '@app/api/routes/externalProductsCatalog/dtos'

const NOT_FOUND_FIELD = VALUE_PLACEHOLDER.notFound

const renderValue = (keyExist: boolean, value: string | null | undefined): string => {
  // TODO полностью откажемся от NOT_FOUND_FIELD. Все поля должны приходить с бэка.
  // Если не будет значения поля, то рисуем черточку, эта обработка уже есть в src/components/DashedTitleValueRow/DashedTitleValueRow.tsx
  // Пока что оставляю так как требуется доработка под дизайн и временно удобно проверить есть ли поле
  if (!keyExist) return NOT_FOUND_FIELD

  return value?.length ? value : VALUE_PLACEHOLDER.doubleDash
}

const getPhotos = (photos: MonitoringPhotos[]): LntImageProps[] => {
  return photos.map(photoItem => ({
    src: photoItem.photo
  }))
}

// TODO: ! нужен рефакторинг этой логики
export const adaptProcessingResultsData = (
  monitoringTaskResultItems: MonitoringTaskResultItemsDto
): ProcessingResultsData | undefined => {
  const { externalProduct } = monitoringTaskResultItems

  const getExternalProductDataSource = (product: ExternalProductsListDto | null): ExternalProduct | null => {
    if (!product) return null

    return {
      id: product.id,
      status: getExternalProductStatus(product)
    }
  }

  const generateTitle = (): string => {
    const productCode = monitoringTaskResultItems.product.code ?? ''
    const name = monitoringTaskResultItems.product.name ?? ''
    const productForm = monitoringTaskResultItems.productForm ?? ''

    return `${productCode}, ${name ?? productForm ?? ''}`
  }

  return {
    monitoringTypeId: monitoringTaskResultItems.task.monitoringType.id,
    title: generateTitle(),
    externalProduct: getExternalProductDataSource(externalProduct),
    abnormalState: monitoringTaskResultItems.abnormalState,
    images: getPhotos(monitoringTaskResultItems.photos),
    monitoringCharacteristics: {
      monitoringSubtype: renderValue(
        objectHas(monitoringTaskResultItems.task?.monitoringSubtype, 'name'),
        monitoringTaskResultItems.task?.monitoringSubtype?.name
      ),
      monitoringType: renderValue(
        objectHas(monitoringTaskResultItems.task?.monitoringType, 'name'),
        monitoringTaskResultItems.task?.monitoringType?.name
      ),
      cluster: renderValue(
        objectHas(monitoringTaskResultItems.task?.cluster, 'name'),
        monitoringTaskResultItems.task?.cluster?.name
      ),
      store: renderValue(
        objectHas(monitoringTaskResultItems.matchedStore, 'name'),
        monitoringTaskResultItems.matchedStore?.name
      ),
      contractor: renderValue(
        objectHas(monitoringTaskResultItems.task?.contractor, 'name'),
        monitoringTaskResultItems.task?.contractor?.name
      ),
      competitor: renderValue(
        objectHas(monitoringTaskResultItems.competitor, 'text'),
        monitoringTaskResultItems.competitor?.text
      ),
      startPromoDateAt: renderValue(
        objectHas(monitoringTaskResultItems, 'startPromoDateAt'),
        monitoringTaskResultItems.startPromoDateAt
      ), // TODO format
      endPromoDateAt: renderValue(
        objectHas(monitoringTaskResultItems, 'endPromoDateAt'),
        monitoringTaskResultItems.endPromoDateAt
      ), // TODO format
      storeRegularPrice: monitoringTaskResultItems?.storeRegularPrice ?? null,
      storePromoPrice: monitoringTaskResultItems?.storePromoPrice ?? null,
      howToMonitor: renderValue(
        objectHas(monitoringTaskResultItems, 'howToMonitor'),
        monitoringTaskResultItems.howToMonitor
      )
    },
    pricesBlock: {
      regularPrice: monitoringTaskResultItems.regularPrice,
      promoPrice: monitoringTaskResultItems.promoPrice,
      originalPrice: monitoringTaskResultItems.originalPrice,
      note: monitoringTaskResultItems.note,
      discount: monitoringTaskResultItems.discount,
      discountMechanics: monitoringTaskResultItems.discountMechanics
    },
    productCharacteristics: {
      trend: renderValue(
        objectHas(monitoringTaskResultItems.product.subcategory.purchasingGroup, 'trend'),
        monitoringTaskResultItems.product.subcategory.purchasingGroup.trend.id
      ),
      purchasingGroup: renderValue(
        objectHas(monitoringTaskResultItems.product.subcategory, 'purchasingGroup'),
        monitoringTaskResultItems.product.subcategory.purchasingGroup.id
      ),
      productGroup: renderValue(
        objectHas(monitoringTaskResultItems.product.subcategory.category, 'commodityGroup'),
        monitoringTaskResultItems.product.subcategory.category.commodityGroup.id
      ),
      category: renderValue(
        objectHas(monitoringTaskResultItems.product.subcategory, 'category'),
        monitoringTaskResultItems.product.subcategory.category.id
      ),
      subcategory: renderValue(
        objectHas(monitoringTaskResultItems.product, 'subcategory'),
        monitoringTaskResultItems.product.subcategory.id
      ),
      brand: renderValue(objectHas(monitoringTaskResultItems, 'brand'), monitoringTaskResultItems.brand),
      manufacturer: renderValue(
        objectHas(monitoringTaskResultItems, 'manufacturer'),
        monitoringTaskResultItems.manufacturer
      ),
      regionManufacturer: renderValue(
        objectHas(monitoringTaskResultItems, 'regionManufacturer'),
        monitoringTaskResultItems.regionManufacturer
      ),
      categoryEggs: renderValue(
        objectHas(monitoringTaskResultItems, 'categoryEggs'),
        monitoringTaskResultItems.categoryEggs
      ),
      shell: renderValue(objectHas(monitoringTaskResultItems, 'shell'), monitoringTaskResultItems.shell),
      fatContentOrFortress: renderValue(
        objectHas(monitoringTaskResultItems, 'fatContentOrFortress'),
        monitoringTaskResultItems.fatContentOrFortress
      ),
      packingVolume: renderValue(
        objectHas(monitoringTaskResultItems, 'packingVolume'),
        monitoringTaskResultItems.packingVolume
      ),
      productForm: renderValue(
        objectHas(monitoringTaskResultItems, 'productForm'),
        monitoringTaskResultItems.productForm
      ),
      packing: renderValue(objectHas(monitoringTaskResultItems, 'packing'), monitoringTaskResultItems.packing),
      isRecalculation: monitoringTaskResultItems.isRecalculation ? 'Да' : 'Нет',
      productRating: renderValue(
        objectHas(monitoringTaskResultItems, 'productRating'),
        monitoringTaskResultItems.productRating
      ),
      barcodes: monitoringTaskResultItems.product.barcodes?.join(', ') ?? NOT_FOUND_FIELD
    }
  }
}

export const adaptCommentData = (
  monitoringTaskResultItemCommentsItem: MonitoringTaskResultItemCommentsItemDto[]
): CommentData[] => {
  return monitoringTaskResultItemCommentsItem.map(dto => ({
    id: dto.id,
    message: dto.message ?? null,
    samples: dto.samples ?? null,
    author: dto.author ?? null,
    editor: dto.editor ?? null,
    createdAt: getFormattedDate(dto.createdAt, dateFormat.DD_MM_YYYY_HH_mm),
    updatedAt: getFormattedDate(dto.updatedAt, dateFormat.DD_MM_YYYY_HH_mm),
    systemComments: dto.systemComments ?? null,
    isSystemComment: !!dto.systemComments?.length
  }))
}

export const adaptPatchTaskResultsItemData = (formData: FormResultsItemPriceData): PatchTaskResultsItemData => {
  return {
    prices: {
      regularPrice: formData.regularPrice ?? null,
      promoPrice: formData.promoPrice ?? null
    },
    comment: {
      sampleIds: formData.sampleIds.length ? formData.sampleIds : undefined,
      message: formData.message.length ? formData.message : undefined
    }
  }
}
